import React, { useState, useEffect } from 'react'
import { 
  CurrencyDollarIcon, 
  ShoppingCartIcon, 
  ClockIcon, 
  ChartBarIcon,
  TableCellsIcon,
  ExclamationTriangleIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  EyeIcon
} from '@heroicons/react/24/outline'
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, PieChart, Pie, Cell } from 'recharts'
import apiService from '../services/api'

interface RestaurantSettings {
  currency: string
  currency_symbol: string
  currency_display_type: 'text' | 'icon'
  currency_display_position: 'left' | 'right'
  currency_icon_url: string
}

interface DashboardStats {
  todayRevenue: number
  todayOrders: number
  avgOrderValue: number
  activeTables: number
  totalTables: number
  pendingOrders: number
  lowStockItems: number
  revenueChange: number
  ordersChange: number
}

const Dashboard: React.FC = () => {
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState<DashboardStats>({
    todayRevenue: 0,
    todayOrders: 0,
    avgOrderValue: 0,
    activeTables: 0,
    totalTables: 0,
    pendingOrders: 0,
    lowStockItems: 0,
    revenueChange: 0,
    ordersChange: 0
  })

  const [chartData, setChartData] = useState<Array<{ name: string; revenue: number; orders: number }>>([])
  const [recentOrders, setRecentOrders] = useState<Array<{ id: string; table: string; total: number; status: string; time: string; items: number }>>([])
  const [topProducts, setTopProducts] = useState<Array<{ name: string; sales: number; revenue: number }>>([])
  const [restaurantSettings, setRestaurantSettings] = useState<RestaurantSettings>({
    currency: 'SAR',
    currency_symbol: 'ر.س',
    currency_display_type: 'text',
    currency_display_position: 'left',
    currency_icon_url: ''
  })

  useEffect(() => {
    loadRestaurantSettings()
    loadDashboardData()
    
    // Auto-refresh every 30 seconds
    const interval = setInterval(() => {
      loadDashboardData(false)
    }, 30000)
    
    return () => clearInterval(interval)
  }, [])

  const loadRestaurantSettings = async () => {
    try {
      const response = await apiService.getRestaurantSettings()
      if (response.success && response.data) {
        const settings = response.data as unknown as RestaurantSettings
        setRestaurantSettings({
          currency: settings.currency || 'SAR',
          currency_symbol: settings.currency_symbol || 'ر.س',
          currency_display_type: settings.currency_display_type || 'text',
          currency_display_position: settings.currency_display_position || 'left',
          currency_icon_url: settings.currency_icon_url || ''
        })
      }
    } catch (error) {
      console.error('Error loading restaurant settings:', error)
    }
  }

  const formatCurrency = (amount: number) => {
    const formattedAmount = parseFloat(amount.toFixed(2)).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })
    
    const currencySymbol = restaurantSettings.currency_symbol || 'ر.س'
    
    if (restaurantSettings.currency_display_type === 'icon' && restaurantSettings.currency_icon_url) {
      if (restaurantSettings.currency_display_position === 'left') {
        return (
          <>
            <img src={restaurantSettings.currency_icon_url} alt="Currency" className="inline-block w-4 h-4 mr-1" />
            {formattedAmount}
          </>
        )
      } else {
        return (
          <>
            {formattedAmount}
            <img src={restaurantSettings.currency_icon_url} alt="Currency" className="inline-block w-4 h-4 ml-1" />
          </>
        )
      }
    } else {
      if (restaurantSettings.currency_display_position === 'left') {
        return `${currencySymbol}${formattedAmount}`
      } else {
        return `${formattedAmount}${currencySymbol}`
      }
    }
  }

  const formatCurrencyAsString = (amount: number) => {
    const formattedAmount = parseFloat(amount.toFixed(2)).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })
    const currencySymbol = restaurantSettings.currency_symbol || 'ر.س'
    if (restaurantSettings.currency_display_position === 'left') {
      return `${currencySymbol}${formattedAmount}`
    } else {
      return `${formattedAmount}${currencySymbol}`
    }
  }

  const loadDashboardData = async (showLoading = true) => {
    if (showLoading) {
      setLoading(true)
    }
    try {
      const response = await apiService.getDashboardStats()
      if (response.success && response.data) {
        const data = response.data as {
          stats: DashboardStats
          chartData: Array<{ name: string; revenue: number; orders: number }>
          recentOrders: Array<{ id: string; table: string; total: number; status: string; time: string; items: number }>
          topProducts: Array<{ name: string; sales: number; revenue: number }>
        }
        const { stats: statsData, chartData: chartDataFromApi, recentOrders: recentOrdersFromApi, topProducts: topProductsFromApi } = data
        
        setStats({
          todayRevenue: statsData.todayRevenue || 0,
          todayOrders: statsData.todayOrders || 0,
          avgOrderValue: statsData.avgOrderValue || 0,
          activeTables: statsData.activeTables || 0,
          totalTables: statsData.totalTables || 0,
          pendingOrders: statsData.pendingOrders || 0,
          lowStockItems: statsData.lowStockItems || 0,
          revenueChange: statsData.revenueChange || 0,
          ordersChange: statsData.ordersChange || 0
        })
        
        setChartData(chartDataFromApi || [])
        setRecentOrders(recentOrdersFromApi || [])
        setTopProducts(topProductsFromApi || [])
      }
    } catch (error) {
      console.error('Error loading dashboard data:', error)
    } finally {
      if (showLoading) {
        setLoading(false)
      }
    }
  }

  const StatCard = ({ title, value, icon: Icon, change, changeType, color = 'blue', isCurrency = false }: any) => (
    <div className="bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg hover:shadow-lg transition-shadow">
      <div className="p-5">
        <div className="flex items-center">
          <div className="flex-shrink-0">
            <Icon className={`h-6 w-6 text-${color}-600`} />
          </div>
          <div className="ml-5 w-0 flex-1">
            <dl>
              <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                {title}
              </dt>
              <dd className="flex items-baseline">
                <div className="text-2xl font-semibold text-gray-900 dark:text-white">
                  {isCurrency ? formatCurrency(value) : (typeof value === 'number' ? value.toLocaleString() : value)}
                </div>
                {change !== undefined && change !== null && (
                  <div className={`ml-2 flex items-baseline text-sm font-semibold ${
                    changeType === 'increase' ? 'text-green-600' : changeType === 'decrease' ? 'text-red-600' : 'text-gray-600'
                  }`}>
                    {changeType === 'increase' ? (
                      <ArrowUpIcon className="self-center flex-shrink-0 h-4 w-4" />
                    ) : changeType === 'decrease' ? (
                      <ArrowDownIcon className="self-center flex-shrink-0 h-4 w-4" />
                    ) : null}
                    <span className="sr-only">{changeType === 'increase' ? 'Increased' : changeType === 'decrease' ? 'Decreased' : 'Changed'} by</span>
                    {Math.abs(change).toFixed(1)}%
                  </div>
                )}
              </dd>
            </dl>
          </div>
        </div>
      </div>
    </div>
  )

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'text-green-600 bg-green-100 dark:bg-green-900 dark:text-green-200'
      case 'preparing': return 'text-yellow-600 bg-yellow-100 dark:bg-yellow-900 dark:text-yellow-200'
      case 'pending': return 'text-red-600 bg-red-100 dark:bg-red-900 dark:text-red-200'
      default: return 'text-gray-600 bg-gray-100 dark:bg-gray-700 dark:text-gray-300'
    }
  }

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8']

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="md:flex md:items-center md:justify-between">
        <div className="flex-1 min-w-0">
          <h2 className="text-2xl font-bold leading-7 text-gray-900 dark:text-white sm:text-3xl sm:truncate">
            Dashboard
          </h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Welcome back! Here's what's happening at your restaurant today.
          </p>
        </div>
        <div className="mt-4 flex space-x-3 md:mt-0 md:ml-4">
          <button
            type="button"
            onClick={() => loadDashboardData(true)}
            className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <EyeIcon className="-ml-1 mr-2 h-5 w-5" />
            Refresh
          </button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
        <StatCard
          title="Today's Revenue"
          value={stats.todayRevenue}
          icon={CurrencyDollarIcon}
          change={stats.revenueChange}
          changeType={stats.revenueChange > 0 ? 'increase' : stats.revenueChange < 0 ? 'decrease' : null}
          color="green"
          isCurrency={true}
        />
        <StatCard
          title="Today's Orders"
          value={stats.todayOrders}
          icon={ShoppingCartIcon}
          change={stats.ordersChange}
          changeType={stats.ordersChange > 0 ? 'increase' : stats.ordersChange < 0 ? 'decrease' : null}
          color="blue"
        />
        <StatCard
          title="Avg Order Value"
          value={stats.avgOrderValue}
          icon={ChartBarIcon}
          change={null}
          changeType={null}
          color="purple"
          isCurrency={true}
        />
        <StatCard
          title="Active Tables"
          value={`${stats.activeTables}/${stats.totalTables}`}
          icon={TableCellsIcon}
          change={null}
          changeType={null}
          color="indigo"
        />
        <StatCard
          title="Pending Orders"
          value={stats.pendingOrders}
          icon={ClockIcon}
          change={null}
          changeType={null}
          color="yellow"
        />
        <StatCard
          title="Low Stock Items"
          value={stats.lowStockItems}
          icon={ExclamationTriangleIcon}
          change={null}
          changeType={null}
          color="red"
        />
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Revenue Chart */}
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
              Revenue Trend (Last 7 Days)
            </h3>
            <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
              <div className="w-3 h-3 bg-blue-500 rounded-full mr-2"></div>
              Revenue
            </div>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis dataKey="name" stroke="#6B7280" />
              <YAxis stroke="#6B7280" />
              <Tooltip 
                formatter={(value: any) => [formatCurrencyAsString(value), 'Revenue']}
                contentStyle={{
                  backgroundColor: '#1F2937',
                  border: '1px solid #374151',
                  borderRadius: '8px',
                  color: '#F9FAFB'
                }}
              />
              <Line type="monotone" dataKey="revenue" stroke="#3B82F6" strokeWidth={3} dot={{ fill: '#3B82F6', strokeWidth: 2, r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Orders Chart */}
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
              Orders Trend (Last 7 Days)
            </h3>
            <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
              <div className="w-3 h-3 bg-green-500 rounded-full mr-2"></div>
              Orders
            </div>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis dataKey="name" stroke="#6B7280" />
              <YAxis stroke="#6B7280" />
              <Tooltip 
                contentStyle={{
                  backgroundColor: '#1F2937',
                  border: '1px solid #374151',
                  borderRadius: '8px',
                  color: '#F9FAFB'
                }}
              />
              <Bar dataKey="orders" fill="#10B981" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Bottom Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Orders */}
        <div className="lg:col-span-2 bg-white dark:bg-gray-800 shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                Recent Orders
              </h3>
              <button className="text-sm text-blue-600 hover:text-blue-500 dark:text-blue-400">
                View All
              </button>
            </div>
          </div>
          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {recentOrders.length > 0 ? (
              recentOrders.map((order) => (
                <div key={order.id} className="px-6 py-4 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                  <div className="flex items-center">
                    <div>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">
                        {order.id}
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {order.table} • {order.items} items • {order.time}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      {formatCurrency(order.total)}
                    </span>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(order.status)}`}>
                      {order.status}
                    </span>
                  </div>
                </div>
              ))
            ) : (
              <div className="px-6 py-8 text-center text-gray-500 dark:text-gray-400">
                No recent orders
              </div>
            )}
          </div>
        </div>

        {/* Top Products */}
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
              Top Products
            </h3>
          </div>
          <div className="p-6">
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie
                  data={topProducts.slice(0, 5)}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }: any) => `${name} ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="sales"
                >
                  {topProducts.slice(0, 5).map((_entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip 
                  formatter={(value, name) => [`${value} sales`, name]}
                  contentStyle={{
                    backgroundColor: '#1F2937',
                    border: '1px solid #374151',
                    borderRadius: '8px',
                    color: '#F9FAFB'
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
            <div className="mt-4 space-y-2">
              {topProducts.length > 0 ? (
                topProducts.map((product, index) => (
                  <div key={product.name} className="flex items-center justify-between text-sm">
                    <div className="flex items-center">
                      <div 
                        className="w-3 h-3 rounded-full mr-2" 
                        style={{ backgroundColor: COLORS[index % COLORS.length] }}
                      />
                      <span className="text-gray-900 dark:text-white truncate">{product.name}</span>
                    </div>
                    <div className="text-right">
                      <div className="text-gray-900 dark:text-white font-medium">{product.sales}</div>
                      <div className="text-gray-500 dark:text-gray-400 text-xs">{formatCurrency(product.revenue)}</div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center text-gray-500 dark:text-gray-400 py-4">
                  No product data available
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Dashboard
